import { TableStatus, type TableInfo } from '@/types/api';

export type PendingTableOp =
  | { type: 'create'; tempId: number; name: string; capacity: number }
  | { type: 'assign'; tempId: number; posTableId: number; sourceTable: TableInfo }
  | { type: 'detach'; tableId: number }
  | { type: 'delete'; tableId: number };

/**
 * Combines the real (already-persisted) tables with pending local edits for display on the
 * canvas: pending creates/assigns render as synthetic entries keyed by a temp (negative) id so
 * they're draggable/resizable through the same code path as real tables before being saved;
 * pending deletes/detaches are excluded so they disappear immediately rather than waiting for
 * "Save Layout".
 */
export function mergeTablesForDisplay(tables: TableInfo[], pendingOps: PendingTableOp[]): TableInfo[] {
  const removedIds = new Set(
    pendingOps
      .filter((op): op is Extract<PendingTableOp, { type: 'detach' | 'delete' }> =>
        op.type === 'detach' || op.type === 'delete'
      )
      .map((op) => op.tableId)
  );

  const visible = tables.filter((t) => !removedIds.has(t.id));

  const synthetic: TableInfo[] = pendingOps
    .filter((op): op is Extract<PendingTableOp, { type: 'create' | 'assign' }> =>
      op.type === 'create' || op.type === 'assign'
    )
    .map((op) =>
      op.type === 'create'
        ? {
            id: op.tempId,
            restaurantId: 0,
            tableNumber: op.name,
            section: '',
            capacity: op.capacity,
            status: TableStatus.AVAILABLE,
            updatedAt: '',
          }
        : { ...op.sourceTable, id: op.tempId }
    );

  return [...visible, ...synthetic];
}
